import React, { useState, useEffect } from "react";
import { db } from "../utils/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import {
  History,
  X,
  CheckCircle,
  ChevronDown,
  User,
  Award,
  Bookmark,
  Clock,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  Search,
  Filter,
  Phone,
  MessageSquare,
  Activity,
  Info,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { useAuth } from "../hooks/AuthenticationProvider";
import { Spinner } from "../components";
import { useNavigate } from "react-router-dom";

// Step 4 inventory options
const STEP_4_INVENTORIES = [
  {
    id: 0,
    name: "Not Started",
    color: "bg-red-500/10 text-red-400",
    icon: AlertCircle,
  },
  {
    id: 1,
    name: "Resentments",
    color: "bg-amber-500/10 text-amber-400",
    icon: Activity,
  },
  {
    id: 2,
    name: "Fears",
    color: "bg-purple-500/10 text-purple-400",
    icon: Activity,
  },
  {
    id: 3,
    name: "Sex Conduct",
    color: "bg-pink-500/10 text-pink-400",
    icon: Activity,
  },
  {
    id: 4,
    name: "Harm Done to Others",
    color: "bg-blue-500/10 text-blue-400",
    icon: Activity,
  },
  {
    id: 5,
    name: "Completed All Inventories",
    color: "bg-emerald-500/10 text-emerald-400",
    icon: CheckCircle,
  },
];

// Format phone number in (XXX) XXX-XXXX format
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  const cleaned = phoneNumber.replace(/\D/g, "");

  if (cleaned.length >= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10
    )}`;
  }
  return cleaned;
};

// Card for displaying progress that animates on change
const ProgressIndicator = ({
  label,
  value,
  previousValue,
  max = 12,
  showNumbers = true,
}) => {
  const difference = value - previousValue;
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const getColor = () => {
    if (difference > 0) return "text-emerald-400";
    if (difference < 0) return "text-red-400";
    return "text-gray-400";
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        {difference !== 0 && (
          <span
            className={`${getColor()} text-xs font-medium flex items-center`}
          >
            {difference > 0 ? (
              <ArrowUp size={12} className="mr-1" />
            ) : (
              <ArrowDown size={12} className="mr-1" />
            )}
            {Math.abs(difference)}
          </span>
        )}
      </div>
      <div className="relative h-2.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div
          className={`absolute h-full left-0 top-0 rounded-full transition-all duration-500 ${
            difference >= 0
              ? "bg-gradient-to-r from-cyan-500 to-blue-500"
              : "bg-gradient-to-r from-amber-500 to-red-500"
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showNumbers && (
        <div className="mt-2 flex justify-between items-center">
          <span className="text-xs text-slate-400">0</span>
          <span className="text-xs text-white font-medium">
            {value}/{max}
          </span>
        </div>
      )}
    </div>
  );
};

// History modal with improved UI
const StepHistoryModal = ({ isOpen, onClose, historyData, residentName }) => {
  const [expandedItems, setExpandedItems] = useState([]);

  const toggleExpand = (index) => {
    setExpandedItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Inventory lookup helper
  const getInventoryDetails = (step, columnId) => {
    if (step !== 4 || columnId === undefined || columnId === null) {
      return { name: "-", color: "text-slate-400" };
    }

    const inventory = STEP_4_INVENTORIES.find((inv) => inv.id === columnId) || {
      name: "-",
      color: "text-slate-400",
    };

    return inventory;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] touch-none transition-opacity duration-300 ease-in-out"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 touch-auto">
        <div
          className="bg-slate-900/95 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] my-auto overflow-hidden transition-all duration-300 ease-in-out"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-900/95 backdrop-blur-sm z-[102]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-xl">
                <History className="text-blue-400" size={22} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  Progress History
                </h3>
                {residentName && (
                  <p className="text-sm text-slate-400">{residentName}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="relative z-[103] p-2 hover:bg-slate-800 rounded-lg transition-colors touch-auto focus:outline-none focus:ring-2 focus:ring-slate-700"
            >
              <X className="text-slate-400 hover:text-slate-300" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800/30">
            {historyData.length === 0 ? (
              <div className="text-center py-16">
                <div className="flex justify-center mb-4">
                  <Clock className="text-slate-600" size={48} />
                </div>
                <p className="text-slate-400 text-lg font-medium">
                  No history available
                </p>
                <p className="text-slate-500 text-sm mt-1">
                  Progress updates will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {historyData
                  .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate())
                  .map((update, index) => {
                    const date = update.timestamp.toDate();
                    const inventory = getInventoryDetails(
                      update.step,
                      update.column
                    );

                    return (
                      <div
                        key={index}
                        className="rounded-xl overflow-hidden border border-slate-700/70 bg-gradient-to-b from-slate-800/80 to-slate-900/80"
                        style={{
                          opacity: 1,
                          transform: "translateY(0)",
                          transition: `opacity 0.3s ease, transform 0.3s ease ${
                            index * 0.05
                          }s`,
                        }}
                      >
                        <div
                          onClick={() => update.note && toggleExpand(index)}
                          className={`p-4 ${
                            update.note
                              ? "cursor-pointer hover:bg-slate-800/50 transition-colors"
                              : ""
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="bg-blue-500/10 text-blue-400 p-2 rounded-xl">
                              <Award size={18} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between">
                                <div>
                                  <p className="text-white font-medium">
                                    Step {update.step}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    {date.toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                                    {" · "}
                                    {date.toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </p>
                                </div>

                                {update.step === 4 &&
                                  update.column !== null &&
                                  update.column !== undefined && (
                                    <div
                                      className={`${
                                        STEP_4_INVENTORIES.find(
                                          (inv) => inv.id === update.column
                                        )?.color ||
                                        "bg-slate-700/50 text-slate-400"
                                      } px-3 py-1 rounded-full text-xs font-medium`}
                                    >
                                      {STEP_4_INVENTORIES.find(
                                        (inv) => inv.id === update.column
                                      )?.name || "Unknown"}
                                    </div>
                                  )}
                              </div>

                              <div className="mt-2 flex items-center text-xs text-slate-300">
                                <User size={12} className="mr-1.5" />
                                <span className="truncate">
                                  {update.updatedBy}
                                </span>
                              </div>

                              {update.note && (
                                <div className="mt-2 flex items-center text-xs text-slate-400">
                                  <MessageSquare size={12} className="mr-1.5" />
                                  <span className="truncate">
                                    {expandedItems.includes(index)
                                      ? "Click to collapse"
                                      : "Click to expand note"}
                                  </span>
                                  <ChevronDown
                                    size={14}
                                    className={`ml-1 transition-transform duration-200 ${
                                      expandedItems.includes(index)
                                        ? "rotate-180"
                                        : ""
                                    }`}
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          {expandedItems.includes(index) && update.note && (
                            <div className="mt-3 pt-3 border-t border-slate-700/50 overflow-hidden transition-all duration-300">
                              <p className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">
                                {update.note}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Toast notification component
const Toast = ({ message, type = "success", visible, onClose }) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  const icons = {
    success: <CheckCircle size={20} />,
    error: <AlertCircle size={20} />,
    info: <Info size={20} />,
  };

  const colors = {
    success: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    error: "bg-red-500/20 text-red-400 border-red-500/30",
    info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 top-8 transform -translate-x-1/2 z-[200] pointer-events-auto transition-all duration-300"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate(-50%, 0)" : "translate(-50%, -20px)",
      }}
    >
      <div
        className={`px-6 py-4 rounded-xl shadow-lg border flex items-center gap-3 ${colors[type]}`}
      >
        <div
          className={`${
            type === "success"
              ? "bg-emerald-500/20"
              : type === "error"
              ? "bg-red-500/20"
              : "bg-blue-500/20"
          } rounded-full p-2 backdrop-blur-md`}
        >
          {icons[type]}
        </div>
        <div>
          <p className="font-medium text-white">{message.title}</p>
          {message.description && (
            <p className="text-sm opacity-80">{message.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Sponsorship component
const Sponsorship = () => {
  const { user, isLoading, organizationId, organizationData } = useAuth();
  const navigate = useNavigate();
  const housesData = organizationData?.housesData || {};
  const residents = organizationData?.residents || [];

  // State management
  const [selectedHouse, setSelectedHouse] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentResidents, setCurrentResidents] = useState([]);
  const [formState, setFormState] = useState({});
  const [isLoadingSponsorship, setIsLoadingSponsorship] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState({});
  const [toast, setToast] = useState({
    visible: false,
    message: {},
    type: "success",
  });

  // History modal state
  const [stepHistoryModalOpen, setStepHistoryModalOpen] = useState(false);
  const [stepHistoryData, setStepHistoryData] = useState([]);
  const [currentResidentName, setCurrentResidentName] = useState("");

  // Progress tracking data
  const [stepDifferences, setStepDifferences] = useState({});
  const [columnDifferences, setColumnDifferences] = useState({});
  const [previousValues, setPreviousValues] = useState({});

  // Load data when component mounts
  useEffect(() => {
    // Don't proceed if we don't have an organizationId
    if (!organizationId) {
      return;
    }

    let isMounted = true;

    const fetchAllSponsorship = async (residents) => {
      try {
        const sponsorshipMap = {};
        const stepDiffs = {};
        const columnDiffs = {};
        const prevValues = {};

        // Process each resident individually to fetch their sponsorship data
        await Promise.all(
          residents.map(async (resident) => {
            // Reference to the resident's sponsorship subcollection
            const sponsorshipRef = doc(
              db,
              `organizations/${organizationId}/residents/${resident.id}/sponsorship/data`
            );

            const sponsorshipDoc = await getDoc(sponsorshipRef);

            if (sponsorshipDoc.exists()) {
              const data = sponsorshipDoc.data();
              sponsorshipMap[resident.id] = data;

              // Calculate differences if there's enough history
              if (data.stepHistory && data.stepHistory.length > 1) {
                const sortedHistory = [...data.stepHistory].sort(
                  (a, b) => b.timestamp.toDate() - a.timestamp.toDate()
                );

                const currentStep = data.currentStep || 0;
                const previousStep = sortedHistory[1].step;
                stepDiffs[resident.id] = currentStep - previousStep;
                prevValues[resident.id] = { step: previousStep };

                // Calculate column difference for Step 4
                if (currentStep === 4) {
                  const currentColumn = data.currentColumn || 0;
                  const previousColumn = sortedHistory[1].column || 0;
                  columnDiffs[resident.id] = currentColumn - previousColumn;
                  prevValues[resident.id].column = previousColumn;
                }
              } else if (data.stepHistory && data.stepHistory.length === 1) {
                // If only one history entry exists
                prevValues[resident.id] = {
                  step: 0,
                  column: 0,
                };
              }
            }
          })
        );

        const sponsorshipData = {};
        residents.forEach((resident) => {
          const data = sponsorshipMap[resident.id];
          if (data) {
            sponsorshipData[resident.id] = {
              sponsorName: data.sponsorName || "",
              sponsorNumber: data.sponsorNumber
                ? formatPhoneNumber(data.sponsorNumber)
                : "",
              currentStep: (data.currentStep || 0).toString(),
              currentColumn: data.currentColumn
                ? data.currentColumn.toString()
                : "0",
              note: "",
            };
          } else {
            // Initialize data for new residents
            sponsorshipData[resident.id] = {
              sponsorName: "",
              sponsorNumber: "",
              currentStep: "0",
              currentColumn: "0",
              note: "",
            };
            prevValues[resident.id] = { step: 0, column: 0 };
          }
        });

        return { sponsorshipData, stepDiffs, columnDiffs, prevValues };
      } catch (error) {
        console.error("Error fetching sponsorship data:", error);
        return {
          sponsorshipData: {},
          stepDiffs: {},
          columnDiffs: {},
          prevValues: {},
        };
      }
    };

    const loadSponsorshipData = async () => {
      if (isMounted) {
        setIsLoadingSponsorship(true);
      }

      // Filter for current residents first
      const currentResidents = residents.filter(
        (resident) => resident.currentResident === true
      );

      if (!currentResidents.length) {
        if (isMounted) {
          setFormState({});
          setStepDifferences({});
          setColumnDifferences({});
          setPreviousValues({});
          setIsLoadingSponsorship(false);
        }
        return;
      }

      try {
        const { sponsorshipData, stepDiffs, columnDiffs, prevValues } =
          await fetchAllSponsorship(currentResidents);

        if (isMounted) {
          setFormState(sponsorshipData);
          setStepDifferences(stepDiffs);
          setColumnDifferences(columnDiffs);
          setPreviousValues(prevValues);
          setIsLoadingSponsorship(false);
        }
      } catch (error) {
        console.error("Error loading sponsorship data:", error);
        if (isMounted) {
          setFormState({});
          setStepDifferences({});
          setColumnDifferences({});
          setPreviousValues({});
          setIsLoadingSponsorship(false);
        }
      }
    };

    loadSponsorshipData();

    return () => {
      isMounted = false;
    };
  }, [residents, organizationId, organizationData, navigate]);

  // Filter residents based on search query and selected house
  useEffect(() => {
    const lowercaseQuery = searchQuery.toLowerCase();
    const filtered = residents.filter(
      (resident) =>
        resident.currentResident === true &&
        (selectedHouse === "all" || resident.house === selectedHouse) &&
        (searchQuery === "" ||
          resident.firstName.toLowerCase().includes(lowercaseQuery) ||
          resident.lastName.toLowerCase().includes(lowercaseQuery))
    );
    setCurrentResidents(filtered);
  }, [residents, selectedHouse, searchQuery]);

  const getHouseColors = (house) => {
    const houseData = housesData?.[house];
    if (!houseData) return {};

    const color = houseData.color || "#6366f1"; // Default indigo if no color

    return {
      cardStyle: {
        background: `linear-gradient(to bottom, ${color}08, rgba(15, 23, 42, 0.3))`,
        borderColor: `${color}40`,
        boxShadow: `0 4px 20px -8px ${color}20`,
        transition: "all 0.3s ease",
      },
      avatarStyle: {
        background: `linear-gradient(135deg, ${color}30, ${color}10)`,
        borderColor: `${color}40`,
      },
      pillStyle: {
        background: `${color}15`,
        color: color,
        borderColor: `${color}30`,
      },
      houseIndicator: {
        background: color,
      },
    };
  };

  // Update form state when fields change
  const handleFormChange = (residentId, field, value) => {
    if (field === "sponsorNumber") {
      value = formatPhoneNumber(value);
    }

    setFormState((prev) => ({
      ...prev,
      [residentId]: {
        ...prev?.[residentId],
        [field]: value,
      },
    }));
  };

  // Submit form handler
  const handleSubmit = async (resident) => {
    try {
      setIsSubmitting((prev) => ({ ...prev, [resident.id]: true }));
      const residentForm = formState[resident.id] || {};
      const currentStep = parseInt(residentForm.currentStep || "0");

      // Determine if we need to track columns (only for step 4)
      const currentColumn =
        currentStep === 4 ? parseInt(residentForm.currentColumn || "0") : 0;

      // Create the new history entry
      const newUpdate = {
        timestamp: Timestamp.now(),
        step: currentStep,
        column: currentStep === 4 ? currentColumn : null,
        updatedBy: user.email,
        note: residentForm.note || null,
      };

      // Reference to the resident's sponsorship document in their subcollection
      const sponsorshipRef = doc(
        db,
        `organizations/${organizationId}/residents/${resident.id}/sponsorship/data`
      );

      // Get existing document
      const sponsorshipDoc = await getDoc(sponsorshipRef);
      let lastStep = 0;
      let lastColumn = 0;

      if (sponsorshipDoc.exists()) {
        const data = sponsorshipDoc.data();
        if (data.stepHistory && data.stepHistory.length > 0) {
          const sortedHistory = data.stepHistory.sort(
            (a, b) => b.timestamp.toDate() - a.timestamp.toDate()
          );
          lastStep = sortedHistory[0].step;
          lastColumn = sortedHistory[0].column || 0;
        }

        // Update existing document
        await updateDoc(sponsorshipRef, {
          sponsorName: residentForm.sponsorName || "",
          sponsorNumber: residentForm.sponsorNumber
            ? residentForm.sponsorNumber.replace(/\D/g, "")
            : "",
          currentStep: currentStep,
          currentColumn: currentStep === 4 ? currentColumn : null,
          lastUpdated: Timestamp.now(),
          stepHistory: arrayUnion(newUpdate),
        });
      } else {
        // Create new document
        await setDoc(sponsorshipRef, {
          residentId: resident.id,
          residentName: `${resident.firstName} ${resident.lastName}`,
          house: resident.house,
          sponsorName: residentForm.sponsorName || "",
          sponsorNumber: residentForm.sponsorNumber
            ? residentForm.sponsorNumber.replace(/\D/g, "")
            : "",
          currentStep: currentStep,
          currentColumn: currentStep === 4 ? currentColumn : null,
          created: Timestamp.now(),
          lastUpdated: Timestamp.now(),
          stepHistory: [newUpdate],
          organizationId: organizationId,
        });
      }

      // Update form state with new data, but clear the note field
      setFormState((prev) => ({
        ...prev,
        [resident.id]: {
          ...prev[resident.id],
          sponsorName: residentForm.sponsorName || "",
          sponsorNumber: residentForm.sponsorNumber || "",
          currentStep: residentForm.currentStep || "0",
          currentColumn:
            currentStep === 4 ? residentForm.currentColumn || "0" : "0",
          note: "", // Clear the note field
        },
      }));

      // Update previous values for animations
      setPreviousValues((prev) => ({
        ...prev,
        [resident.id]: {
          step: lastStep,
          column: lastColumn,
        },
      }));

      // Update step differences
      setStepDifferences((prev) => ({
        ...prev,
        [resident.id]: currentStep - lastStep,
      }));

      // Update column differences if on step 4
      if (currentStep === 4) {
        setColumnDifferences((prev) => ({
          ...prev,
          [resident.id]: currentColumn - lastColumn,
        }));
      }

      // Show success notification
      setToast({
        visible: true,
        message: {
          title: "Progress Updated",
          description: `${resident.firstName}'s sponsorship information saved successfully`,
        },
        type: "success",
      });

      setIsSubmitting((prev) => ({ ...prev, [resident.id]: false }));
    } catch (error) {
      console.error("Error saving sponsorship record: ", error);
      setToast({
        visible: true,
        message: {
          title: "Update Failed",
          description: "There was an error saving the sponsorship information",
        },
        type: "error",
      });
      setIsSubmitting((prev) => ({ ...prev, [resident.id]: false }));
    }
  };

  // View history for a specific resident
  const viewHistory = async (resident) => {
    try {
      const sponsorshipRef = doc(
        db,
        `organizations/${organizationId}/residents/${resident.id}/sponsorship/data`
      );

      const docSnap = await getDoc(sponsorshipRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const history = data.stepHistory || [];

        // Sort history by timestamp in descending order
        const sortedHistory = [...history].sort(
          (a, b) => b.timestamp.toDate() - a.timestamp.toDate()
        );

        // Set history data
        setStepHistoryData(sortedHistory);
        setCurrentResidentName(`${resident.firstName} ${resident.lastName}`);
        setStepHistoryModalOpen(true);
      } else {
        setStepHistoryData([]);
        setCurrentResidentName(`${resident.firstName} ${resident.lastName}`);
        setStepHistoryModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      setToast({
        visible: true,
        message: {
          title: "Error Loading History",
          description: "Failed to load sponsor history data",
        },
        type: "error",
      });
    }
  };

  // Loading state
  if (isLoading || isLoadingSponsorship) {
    return (
      <div className="flex flex-col items-center justify-center h-[80vh]">
        <Spinner size="large" />
        <p className="mt-4 text-slate-400 animate-pulse">
          Loading sponsorship data...
        </p>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    navigate("/login");
    return null;
  }

  // AA Steps
  const steps = Array.from({ length: 13 }, (_, i) => i); // 0-12 steps

  return (
    <>
      <Helmet>
        <title>• Sponsorship Tracking</title>
      </Helmet>

      {/* Success/Error Toast Notification */}
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, visible: false }))}
      />

      {/* History Modal */}
      <StepHistoryModal
        isOpen={stepHistoryModalOpen}
        onClose={() => setStepHistoryModalOpen(false)}
        historyData={stepHistoryData}
        residentName={currentResidentName}
      />

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 px-3 sm:px-6 py-4 sm:py-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-64 sm:w-96 h-64 sm:h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header with search controls */}
          <div className="flex flex-col space-y-6 mb-8">
            {/* Title section */}
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="flex gap-2 sm:gap-3">
                <div className="h-12 sm:h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
                <div className="h-12 sm:h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
                <div className="h-12 sm:h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">
                  Sponsorship Tracking
                </h1>
                <p className="text-slate-400 text-sm mt-1">
                  Track resident progress through the 12 steps program
                </p>
              </div>
            </div>

            {/* Search and filter controls */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Box */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search residents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 bg-slate-800/50 text-white rounded-lg pl-10 pr-4 py-2 border border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* House Filter */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter size={16} className="text-slate-400" />
                </div>
                <select
                  value={selectedHouse}
                  onChange={(e) => setSelectedHouse(e.target.value)}
                  className="appearance-none w-full sm:w-auto bg-slate-800/50 text-white rounded-lg pl-10 pr-10 py-2 border border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                >
                  <option value="all">All Houses</option>
                  {Object.keys(housesData).map((house) => (
                    <option key={house} value={house}>
                      {house}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronDown size={16} className="text-slate-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Residents</p>
                  <p className="text-2xl font-semibold text-white">
                    {currentResidents.length}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <User className="text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Average Step</p>
                  <p className="text-2xl font-semibold text-white">
                    {currentResidents.length > 0
                      ? (
                          currentResidents.reduce(
                            (sum, resident) =>
                              sum +
                              parseInt(
                                formState[resident.id]?.currentStep || 0
                              ),
                            0
                          ) / currentResidents.length
                        ).toFixed(1)
                      : "0.0"}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <Activity className="text-cyan-400" />
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Residents Completed</p>
                  <p className="text-2xl font-semibold text-white">
                    {
                      currentResidents.filter(
                        (resident) =>
                          parseInt(formState[resident.id]?.currentStep || 0) ===
                          12
                      ).length
                    }{" "}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="text-emerald-400" />
                </div>
              </div>
            </div>
          </div>

          {/* No Results Message */}
          {currentResidents.length === 0 && (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-8 text-center my-8">
              <div className="flex justify-center mb-4">
                <Search size={48} className="text-slate-600" />
              </div>
              <h3 className="text-xl font-medium text-white mb-2">
                No residents found
              </h3>
              <p className="text-slate-400 max-w-md mx-auto">
                {searchQuery
                  ? `No residents matching "${searchQuery}" were found. Try a different search term.`
                  : "No residents found for the selected house. Try selecting a different house."}
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedHouse("all");
                }}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}

          {/* Residents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentResidents.map((resident) => {
              const isStep4 =
                parseInt(formState[resident.id]?.currentStep || "0") === 4;
              const currentStep = parseInt(
                formState[resident.id]?.currentStep || "0"
              );
              const currentColumn = isStep4
                ? parseInt(formState[resident.id]?.currentColumn || "0")
                : 0;
              const prevStep = previousValues[resident.id]?.step || 0;
              const prevColumn = previousValues[resident.id]?.column || 0;
              const isExpanded = expandedUser === resident.id;

              return (
                <div
                  key={resident.id}
                  className={`group relative rounded-xl transition-all duration-300 bg-slate-900/90 border shadow-lg overflow-hidden ${
                    isExpanded
                      ? "ring-2 ring-blue-500"
                      : "hover:border-blue-500/30 hover:shadow-blue-900/5"
                  }`}
                  style={{
                    ...getHouseColors(resident.house).cardStyle,
                    opacity: 1,
                    transform: "translateY(0)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div className="relative p-5 space-y-4">
                    {/* House indicator - thin line at top */}
                    <div
                      className="absolute top-0 left-0 w-full h-0.5 opacity-70"
                      style={{
                        background:
                          housesData?.[resident.house]?.color || "#6366f1",
                      }}
                    ></div>

                    {/* Header with user info */}
                    <div className="flex items-center gap-4">
                      <div
                        className="h-12 w-12 rounded-xl flex items-center justify-center shadow-inner ring-2"
                        style={getHouseColors(resident.house).avatarStyle}
                      >
                        <span className="text-lg font-semibold text-white">
                          {resident.firstName.charAt(0)}
                          {resident.lastName.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white group-hover:text-blue-300 transition-colors">
                          {resident.firstName} {resident.lastName}
                        </h3>
                        <div className="flex items-center text-sm">
                          <div
                            className="flex items-center rounded-full px-2.5 py-0.5 text-xs"
                            style={getHouseColors(resident.house).pillStyle}
                          >
                            <span>{resident.house}</span>
                            {resident.roomNumber && (
                              <span className="ml-1">
                                • Room {resident.roomNumber}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bars */}
                    <div className="grid grid-cols-1 gap-3">
                      <ProgressIndicator
                        label="Step Progress"
                        value={currentStep}
                        previousValue={prevStep}
                      />

                      {isStep4 && (
                        <ProgressIndicator
                          label="Step 4 Inventory Progress"
                          value={currentColumn}
                          previousValue={prevColumn}
                          max={5}
                        />
                      )}
                    </div>

                    {/* Expanded Form */}
                    <div>
                      <button
                        onClick={() =>
                          setExpandedUser(isExpanded ? null : resident.id)
                        }
                        className="text-sm text-blue-400 hover:text-blue-300 flex items-center mt-2 transition-colors focus:outline-none"
                      >
                        {isExpanded ? "Hide details" : "Update progress"}
                        <ChevronDown
                          size={16}
                          className={`ml-1.5 transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {isExpanded && (
                        <div
                          className="mt-4 space-y-4 overflow-hidden transition-all duration-300"
                          style={{
                            opacity: 1,
                            maxHeight: "1000px", // Large enough to contain content
                            transition:
                              "opacity 0.3s ease, max-height 0.3s ease",
                          }}
                        >
                          {/* Sponsor Name */}
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1.5">
                              Sponsor Name
                            </label>
                            <input
                              type="text"
                              className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-colors"
                              value={formState[resident.id]?.sponsorName || ""}
                              onChange={(e) =>
                                handleFormChange(
                                  resident.id,
                                  "sponsorName",
                                  e.target.value
                                )
                              }
                              placeholder="Enter sponsor name"
                            />
                          </div>

                          {/* Sponsor Phone */}
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1.5">
                              Sponsor Phone
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Phone size={14} className="text-slate-500" />
                              </div>
                              <input
                                type="tel"
                                className="w-full bg-slate-800/70 border border-slate-700 rounded-lg pl-10 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-colors"
                                value={
                                  formState[resident.id]?.sponsorNumber || ""
                                }
                                onChange={(e) =>
                                  handleFormChange(
                                    resident.id,
                                    "sponsorNumber",
                                    e.target.value
                                  )
                                }
                                placeholder="(555) 555-5555"
                              />
                            </div>
                          </div>

                          {/* Step Selection */}
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1.5">
                              Current Step
                            </label>
                            <select
                              className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-colors appearance-none"
                              value={formState[resident.id]?.currentStep || "0"}
                              onChange={(e) =>
                                handleFormChange(
                                  resident.id,
                                  "currentStep",
                                  e.target.value
                                )
                              }
                            >
                              {steps.map((step) => (
                                <option key={step} value={step}>
                                  {step === 0 ? "Not Started" : `Step ${step}`}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Step 4 Column Selection (only visible when on Step 4) */}
                          {isStep4 && (
                            <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                                Step 4 Inventory Progress
                              </label>
                              <select
                                className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-colors appearance-none"
                                value={
                                  formState[resident.id]?.currentColumn || "0"
                                }
                                onChange={(e) =>
                                  handleFormChange(
                                    resident.id,
                                    "currentColumn",
                                    e.target.value
                                  )
                                }
                              >
                                {STEP_4_INVENTORIES.map((inventory) => (
                                  <option
                                    key={inventory.id}
                                    value={inventory.id}
                                  >
                                    {inventory.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Note Field */}
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1.5">
                              Update Note
                            </label>
                            <textarea
                              className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-colors resize-none min-h-[80px]"
                              value={formState[resident.id]?.note || ""}
                              onChange={(e) =>
                                handleFormChange(
                                  resident.id,
                                  "note",
                                  e.target.value
                                )
                              }
                              placeholder="Enter note for this update (optional)"
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-3 pt-2">
                            <button
                              onClick={() => handleSubmit(resident)}
                              disabled={isSubmitting[resident.id]}
                              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                              {isSubmitting[resident.id] ? (
                                <>
                                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
                                  Saving...
                                </>
                              ) : (
                                <>Save Progress</>
                              )}
                            </button>
                            <button
                              onClick={() => viewHistory(resident)}
                              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900 flex items-center justify-center"
                            >
                              <History size={14} className="mr-1.5" />
                              View History
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Current Status Pills */}
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {formState[resident.id]?.sponsorName && (
                        <div className="bg-slate-800/80 px-2.5 py-1 rounded-full text-xs text-slate-300 flex items-center">
                          <User size={10} className="mr-1" />
                          {formState[resident.id]?.sponsorName}
                        </div>
                      )}

                      <div
                        className={`${
                          currentStep > 0
                            ? "bg-blue-500/10 text-blue-400"
                            : "bg-red-500/10 text-red-400"
                        } px-2.5 py-1 rounded-full text-xs flex items-center`}
                      >
                        {currentStep > 0 ? (
                          <>
                            <Award size={10} className="mr-1" />
                            Step {currentStep}
                          </>
                        ) : (
                          <>
                            <AlertCircle size={10} className="mr-1" />
                            Not Started
                          </>
                        )}
                      </div>

                      {isStep4 && currentColumn > 0 && (
                        <div
                          className={`${
                            STEP_4_INVENTORIES.find(
                              (inv) => inv.id === currentColumn
                            )?.color
                          } px-2.5 py-1 rounded-full text-xs flex items-center`}
                        >
                          <Bookmark size={10} className="mr-1" />
                          {
                            STEP_4_INVENTORIES.find(
                              (inv) => inv.id === currentColumn
                            )?.name
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination (if needed in the future) */}
          {currentResidents.length > 0 && currentResidents.length > 12 && (
            <div className="mt-8 flex justify-center">
              <div className="inline-flex items-center rounded-lg border border-slate-700 bg-slate-800/50">
                <button
                  className="px-3 py-2 text-sm text-slate-400 hover:text-white disabled:opacity-50"
                  disabled={true}
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-white bg-blue-600 font-medium">
                  1
                </span>
                <button
                  className="px-3 py-2 text-sm text-slate-400 hover:text-white disabled:opacity-50"
                  disabled={true}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sponsorship;
